/**
 * Import API client functions.
 *
 * These functions handle data import operations including validation and processing.
 * All imports are authenticated and use multipart/form-data for file uploads.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// ============================================================================
// Types matching backend DTOs
// ============================================================================

/**
 * Summary of entities to be created or skipped during import.
 */
export interface ImportSummary {
    ledgersToCreate: number;
    valuesToCreate: number;
    valuesToSkip: number;
    returnsToCreate: number;
    returnsToSkip: number;
}

/**
 * Credit cost breakdown for an import operation.
 */
export interface CreditCost {
    ledgers: number;
    values: number;
    returns: number;
    total: number;
}

/**
 * Result of import validation including credit cost verification.
 */
export interface ValidationResult {
    isValid: boolean;
    summary: ImportSummary;
    creditCost: CreditCost;
    userBalance: number;
    hasSufficientCredits: boolean;
    balanceAfterImport: number;
    warnings: string[];
    errors: string[];
}

/**
 * Entities created breakdown by type.
 */
export interface EntitiesCreated {
    ledgers: number;
    values: number;
    returns: number;
}

/**
 * Result of a completed import operation.
 */
export interface ImportResult {
    success: boolean;
    entitiesCreated: EntitiesCreated;
    creditsDeducted: number;
    errors: string[];
}

// ============================================================================
// Error handling
// ============================================================================

/**
 * Error class for import/export API errors.
 */
export class ImportApiError extends Error {
    public readonly status: number;
    public readonly data: unknown;

    constructor(status: number, message: string, data?: unknown) {
        super(message);
        this.name = 'ImportApiError';
        this.status = status;
        this.data = data;
    }
}

/**
 * Handles fetch response errors.
 */
async function handleResponseError(response: Response): Promise<never> {
    if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
    }

    let errorData: unknown;
    try {
        errorData = await response.json();
    } catch {
        errorData = null;
    }

    throw new ImportApiError(
        response.status,
        `Import API request failed: ${response.status}`,
        errorData
    );
}

// ============================================================================
// Generic fetch functions
// ============================================================================

/**
 * Uploads a file to the specified endpoint using multipart/form-data.
 * Returns the JSON response.
 */
async function uploadFile<T>(endpoint: string, file: File, fieldName: string = 'file'): Promise<T> {
    const formData = new FormData();
    formData.append(fieldName, file);

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
        // Note: Do NOT set Content-Type header - browser sets it with boundary for FormData
    });

    if (!response.ok) {
        await handleResponseError(response);
    }

    return response.json();
}

// ============================================================================
// ZIP Import Functions
// ============================================================================

/**
 * Validate a full account ZIP import.
 *
 * Validates the ZIP structure and contents:
 * - Checks for required files (manifest.json, CSV files)
 * - Validates CSV formats
 * - Detects conflicts with existing ledgers by (label + collection + type)
 * - Calculates credit cost for new entities
 *
 * @param file - The ZIP file to validate
 * @returns Validation result with summary, credit cost, and any errors/warnings
 * @throws ImportApiError if the request fails
 */
export async function validateZipImport(file: File): Promise<ValidationResult> {
    return uploadFile<ValidationResult>('/api/import/zip/validate', file);
}

/**
 * Process a full account ZIP import.
 *
 * Imports all data from the ZIP file:
 * - Creates new collections/tags (or reuses existing by name + type match)
 * - Creates new ledgers
 * - Creates values and returns
 * - Deducts credits for new entities
 *
 * This should only be called after successful validation.
 *
 * @param file - The ZIP file to import
 * @returns Import result with created entity counts and credits deducted
 * @throws ImportApiError if the request fails (400 for validation errors, 402 for insufficient credits)
 */
export async function processZipImport(file: File): Promise<ImportResult> {
    return uploadFile<ImportResult>('/api/import/zip/process', file);
}

// ============================================================================
// Values Import Functions
// ============================================================================

/**
 * Validate a values CSV import for a specific ledger.
 *
 * Validates the CSV format and contents:
 * - Required columns: Date, Value, Currency
 * - Optional column: Description
 * - Date format: YYYY-MM-DD
 * - Checks for conflicts by (date + currency) against existing values
 * - Detects duplicates within the file itself
 *
 * @param ledgerId - The ID of the ledger to import values into
 * @param file - The CSV file to validate
 * @returns Validation result with summary, credit cost, and any errors/warnings
 * @throws ImportApiError if the request fails (400 if ledger is archived, 404 if not found)
 */
export async function validateValuesImport(
    ledgerId: number,
    file: File
): Promise<ValidationResult> {
    return uploadFile<ValidationResult>(
        `/api/ledgers/${ledgerId}/values/import/validate`,
        file
    );
}

/**
 * Process a values CSV import for a specific ledger.
 *
 * Creates new ledger values from the CSV file.
 * This should only be called after successful validation.
 *
 * @param ledgerId - The ID of the ledger to import values into
 * @param file - The CSV file to import
 * @returns Import result with created entity counts and credits deducted
 * @throws ImportApiError if the request fails (400 for validation errors, 402 for insufficient credits)
 */
export async function processValuesImport(
    ledgerId: number,
    file: File
): Promise<ImportResult> {
    return uploadFile<ImportResult>(
        `/api/ledgers/${ledgerId}/values/import/process`,
        file
    );
}

// ============================================================================
// Returns Import Functions
// ============================================================================

/**
 * Validate a returns CSV import for a specific ledger.
 *
 * Validates the CSV format and contents:
 * - Required columns: Date, Type, Value, Currency
 * - Optional column: Label
 * - Date format: YYYY-MM-DD
 * - Type: inflow or outflow
 * - Detects exact duplicates by (date + type + value + currency) - these are skipped with warning
 *
 * @param ledgerId - The ID of the ledger to import returns into
 * @param file - The CSV file to validate
 * @returns Validation result with summary, credit cost, and any errors/warnings
 * @throws ImportApiError if the request fails (400 if ledger is archived, 404 if not found)
 */
export async function validateReturnsImport(
    ledgerId: number,
    file: File
): Promise<ValidationResult> {
    return uploadFile<ValidationResult>(
        `/api/ledgers/${ledgerId}/returns/import/validate`,
        file
    );
}

/**
 * Process a returns CSV import for a specific ledger.
 *
 * Creates new ledger returns from the CSV file.
 * Exact duplicates are skipped (not an error).
 * This should only be called after successful validation.
 *
 * @param ledgerId - The ID of the ledger to import returns into
 * @param file - The CSV file to import
 * @returns Import result with created entity counts and credits deducted
 * @throws ImportApiError if the request fails (400 for validation errors, 402 for insufficient credits)
 */
export async function processReturnsImport(
    ledgerId: number,
    file: File
): Promise<ImportResult> {
    return uploadFile<ImportResult>(
        `/api/ledgers/${ledgerId}/returns/import/process`,
        file
    );
}
